import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Users,
  CheckCircle,
  AlertCircle,
  Clock,
  BarChart3,
  Settings,
  UserPlus,
  MapPin,
  Shield,
  Eye,
  EyeOff,
  Download,
  Filter
} from 'lucide-react';

interface DashboardStats {
  totalVoters: number;
  verified: number;
  pending: number;
  inProgress: number;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  constituencies: string[];
  permissions: Record<string, boolean>;
}

interface RoleBasedDashboardProps {
  user: User | null;
}

export default function RoleBasedDashboard({ user }: RoleBasedDashboardProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  
  // Fetch dashboard stats
  const { data: stats, isLoading: statsLoading } = useQuery<DashboardStats>({
    queryKey: ['/api/stats'],
    queryFn: async () => {
      const response = await fetch('/api/stats');
      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }
      return response.json();
    },
  });

  // Fetch user constituencies
  const { data: constituencies } = useQuery<string[]>({
    queryKey: ['/api/users', user?.id, 'constituencies'],
    queryFn: async () => {
      if (!user) return [];
      const response = await fetch(`/api/users/${user.id}/constituencies`);
      if (!response.ok) {
        throw new Error('Failed to fetch constituencies');
      }
      return response.json();
    },
    enabled: !!user,
  });

  // Admin dashboard component
  const AdminDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
              <Users className="h-5 w-5" />
              {t('dashboard.totalVoters')}
              <Badge variant="secondary" className="ml-2">
                {t('roles.admin')}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalVoters.toLocaleString() || '0'}</div>
            <p className={`text-sm text-muted-foreground ${t('language') === 'ta' ? 'tamil-text' : ''}`}>
              {t('dashboard.totalRegistered')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
              <CheckCircle className="h-5 w-5 text-green-600" />
              {t('dashboard.verifiedVoters')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.verified.toLocaleString() || '0'}</div>
            <p className={`text-sm text-muted-foreground ${t('language') === 'ta' ? 'tamil-text' : ''}`}>
              {t('dashboard.successfullyVerified')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
              <AlertCircle className="h-5 w-5 text-orange-600" />
              {t('dashboard.pendingVerification')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats?.pending.toLocaleString() || '0'}</div>
            <p className={`text-sm text-muted-foreground ${t('language') === 'ta' ? 'tamil-text' : ''}`}>
              {t('dashboard.votersPending')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
              <Clock className="h-5 w-5 text-blue-600" />
              {t('dashboard.inProgress')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats?.inProgress.toLocaleString() || '0'}</div>
            <p className={`text-sm text-muted-foreground ${t('language') === 'ta' ? 'tamil-text' : ''}`}>
              {t('dashboard.verificationInProgress')}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>User Management</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button variant="outline" className="w-full">
                <UserPlus className="h-4 w-4 mr-2" />
                Create User
              </Button>
              <Button variant="outline" className="w-full">
                <Settings className="h-4 w-4 mr-2" />
                Manage Roles
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button variant="outline" className="w-full">
                <MapPin className="h-4 w-4 mr-2" />
                Assign Constituencies
              </Button>
              <Button variant="outline" className="w-full">
                <Shield className="h-4 w-4 mr-2" />
                Permissions
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>System Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" className="w-full">
              <Download className="h-4 w-4 mr-2" />
                Export Data
              </Button>
            <Button variant="outline" className="w-full">
                <BarChart3 className="h-4 w-4 mr-2" />
                Analytics
              </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Supervisor dashboard component
  const SupervisorDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              My Constituencies
              <Badge variant="secondary" className="ml-2">
                Supervisor
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalVoters.toLocaleString() || '0'}</div>
            <p className="text-sm text-muted-foreground">Total voters in my constituencies</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Verified Voters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.verified.toLocaleString() || '0'}</div>
            <p className="text-sm text-muted-foreground">Verified voters in my constituencies</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Manage Cadre
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              <UserPlus className="h-4 w-4 mr-2" />
              Create Cadre Account
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              <BarChart3 className="h-4 w-4 mr-2" />
              View Analytics
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Constituency Management</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">My Constituencies</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {constituencies?.map((constituency, index) => (
                  <Card key={index}>
                    <CardHeader>
                      <CardTitle>{constituency}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Assigned Baagams: {user.constituencies?.length || 0}
                        </span>
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4" />
                          View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <Button variant="outline" className="w-full mt-4">
                <MapPin className="h-4 w-4 mr-2" />
                Assign New Constituency
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Cadre dashboard component
  const CadreDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              My Assigned Area
              <Badge variant="secondary" className="ml-2">
                Cadre
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalVoters.toLocaleString() || '0'}</div>
            <p className="text-sm text-muted-foreground">Total voters in my area</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Today's Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Verified</span>
                <span className="text-2xl font-bold text-green-600">{stats?.verified.toLocaleString() || '0'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Pending</span>
                <span className="text-2xl font-bold text-orange-600">{stats?.pending.toLocaleString() || '0'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">In Progress</span>
                <span className="text-2xl font-bold text-blue-600">{stats?.inProgress.toLocaleString() || '0'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" className="w-full">
              <Users className="h-4 w-4 mr-2" />
              View Voter List
            </Button>
            <Button variant="outline" className="w-full">
              <MapPin className="h-4 w-4 mr-2" />
              Start Verification
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Render appropriate dashboard based on user role
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center">Please log in to access the dashboard</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto py-8">
        <div className="mb-6">
          <h1 className={`text-3xl font-bold text-gray-900 ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
            {t('dashboard.welcome', { name: user.name })}
          </h1>
          <p className={`text-gray-600 ${t('language') === 'ta' ? 'tamil-text' : ''}`}>
            {t('dashboard.loggedInAs', { role: t(`roles.${user.role.toLowerCase()}`) })}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview" className={t('language') === 'ta' ? 'tamil-ui' : ''}>
              {t('dashboard.overview')}
            </TabsTrigger>
            {user.role === 'Admin' && (
              <TabsTrigger value="users" className={t('language') === 'ta' ? 'tamil-ui' : ''}>
                {t('dashboard.userManagement')}
              </TabsTrigger>
            )}
            {user.role === 'Admin' && (
              <TabsTrigger value="settings" className={t('language') === 'ta' ? 'tamil-ui' : ''}>
                {t('dashboard.systemSettings')}
              </TabsTrigger>
            )}
            {user.role === 'Supervisor' && (
              <TabsTrigger value="constituencies" className={t('language') === 'ta' ? 'tamil-ui' : ''}>
                {t('dashboard.constituencies')}
              </TabsTrigger>
            )}
            {user.role === 'Supervisor' && (
              <TabsTrigger value="cadre" className={t('language') === 'ta' ? 'tamil-ui' : ''}>
                {t('dashboard.cadreManagement')}
              </TabsTrigger>
            )}
          </TabsList>
          
          <TabsContent value="overview" className="space-y-6">
            {user.role === 'Admin' && <AdminDashboard />}
            {user.role === 'Supervisor' && <SupervisorDashboard />}
            {user.role === 'Cadre' && <CadreDashboard />}
          </TabsContent>
          
          {user.role === 'Admin' && (
            <TabsContent value="users" className="space-y-6">
              <div className="text-2xl font-semibold mb-4">User Management</div>
              {/* User management interface would go here */}
              <div className="text-center text-gray-500">
                User management interface coming soon...
              </div>
            </TabsContent>
          )}
          
          {user.role === 'Admin' && (
            <TabsContent value="settings" className="space-y-6">
              <div className="text-2xl font-semibold mb-4">System Settings</div>
              {/* System settings interface would go here */}
              <div className="text-center text-gray-500">
                System settings interface coming soon...
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
